import { useState, useEffect } from 'react';
import { normalizeDecimalInput, parseDecimalValue, isValidDecimalInput } from '../utils/decimalInput';

interface DecimalInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
  min?: number;
  max?: number;
}

/**
 * Decimal Input component that supports comma (,) as decimal separator
 * Automatically converts dots (.) to commas (,)
 * Preserves user input during typing (e.g., "0," stays as "0," until user continues typing)
 */
export function DecimalInput({ value, onChange, className, placeholder, min, max }: DecimalInputProps) {
  // Internal state to preserve user's exact input (including trailing comma/dot)
  const [displayValue, setDisplayValue] = useState(() => {
    // Initialize with formatted value
    return value === 0 ? '' : normalizeDecimalInput(value);
  });

  // Sync display value when external value changes significantly
  useEffect(() => {
    const currentNumeric = parseDecimalValue(displayValue);
    // Only update if the numeric values differ (avoid overwriting user input)
    if (isNaN(currentNumeric) || Math.abs(currentNumeric - value) > 0.0001) {
      setDisplayValue(value === 0 && displayValue === '' ? '' : normalizeDecimalInput(value));
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow empty input
    if (inputValue === '') {
      setDisplayValue('');
      onChange(0);
      return;
    }

    // Validate input (only allow valid decimal characters)
    if (!isValidDecimalInput(inputValue)) {
      return; // Reject invalid input
    }

    // Normalize dots to commas
    const normalized = normalizeDecimalInput(inputValue);
    
    // Update display value (preserve trailing comma/dot)
    setDisplayValue(normalized);

    // Parse to number and notify parent
    const numericValue = parseDecimalValue(normalized);
    
    // Only update parent if we have a valid number or empty string
    if (!isNaN(numericValue)) {
      // Apply min/max constraints if provided
      let finalValue = numericValue;
      if (min !== undefined && finalValue < min) finalValue = min;
      if (max !== undefined && finalValue > max) finalValue = max;
      
      onChange(finalValue);
    } else if (normalized.endsWith(',') || normalized.endsWith('.')) {
      // User is typing a decimal number (e.g., "0,")
      // Keep current value but allow user to continue typing
      // Don't call onChange yet
    }
  };

  const handleBlur = () => {
    // On blur, format the display value properly
    const numericValue = parseDecimalValue(displayValue);
    if (!isNaN(numericValue)) {
      // Apply min/max constraints
      let finalValue = numericValue;
      if (min !== undefined && finalValue < min) finalValue = min;
      if (max !== undefined && finalValue > max) finalValue = max;
      
      setDisplayValue(finalValue === 0 ? '' : normalizeDecimalInput(finalValue));
      onChange(finalValue);
    } else {
      // Invalid input, reset to 0
      setDisplayValue('');
      onChange(0);
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      placeholder={placeholder || '0'}
    />
  );
}
